import { useCallback, useMemo, useRef } from 'react'
import { Utils } from '@/utils/Utils'
import { SOUND_PATHS, VIBRATE_DURATION_MS } from '@/pages/return/rgr08V2/constants'

/** 오디오·진동 재생 — 화면 배선(`index.tsx`)이 판정 결과에 따라 부른다. */
export interface ScanFeedback {
  playSuccess: () => void
  playError: () => void
}

/**
 * 스캔 판정 오디오·진동 — 1.0 `RGR08.onCheckStock` 동치.
 *
 * `Audio` 인스턴스를 `useRef` 로 1회만 만든다. 1.0 은 스캔마다 `new Audio(...)` 를 새로 만들어
 * 첫 재생이 늦는다.
 *
 * `play()` 는 브라우저에서 프로미스를 반환하지만(자동재생 차단이면 reject) jsdom 스텁은 `undefined` 를
 * 반환한다 — `Promise.resolve()` 로 감싸 둘 다 안전하게 삼킨다. `navigator.vibrate` 는 데스크톱
 * 크롬에 없으므로(1.0 은 무방비라 미지원 브라우저에서 터진다) `?.` 로 호출한다.
 *
 * `play()` 직전에 `currentTime = 0` 으로 되감는다 — 재생 중에 `play()` 를 다시 불러도 브라우저는 현재
 * 위치에서 이어 재생할 뿐 처음부터 다시 울리지 않는다. `success-sound.mp3` 는 약 2초라, 스캐너가 그보다
 * 빠르게 연속 스캔하면 되감지 않을 경우 두 번째 판정에 비프음이 나지 않는다(1.0 은 스캔마다 `new Audio()`
 * 를 새로 만들어 이 문제가 없었다 — 인스턴스 재사용으로 얻은 지연 개선이 되감기 없이는 소리 유실로 이어진다).
 */
export const useScanFeedback = (): ScanFeedback => {
  const successAudioRef = useRef<HTMLAudioElement | null>(null)
  const errorAudioRef = useRef<HTMLAudioElement | null>(null)

  const playSuccess = useCallback(() => {
    successAudioRef.current ??= new Audio(Utils.assetUrl(SOUND_PATHS.success))
    successAudioRef.current.currentTime = 0
    Promise.resolve(successAudioRef.current.play()).catch(() => undefined)
    navigator.vibrate?.(VIBRATE_DURATION_MS)
  }, [])

  const playError = useCallback(() => {
    errorAudioRef.current ??= new Audio(Utils.assetUrl(SOUND_PATHS.error))
    errorAudioRef.current.currentTime = 0
    Promise.resolve(errorAudioRef.current.play()).catch(() => undefined)
    navigator.vibrate?.(VIBRATE_DURATION_MS)
  }, [])

  // 반환 객체를 고정한다 — 두 함수는 `useCallback(fn, [])` 이라 참조가 이미 고정이지만, 객체가 매 렌더
  //  새 참조면 이걸 통째로 deps 에 넣는 호출자가 매 렌더 재실행된다(훅 계약 자체를 안전하게).
  return useMemo(() => ({ playSuccess, playError }), [playSuccess, playError])
}
